import { debounce } from '@cian/ui-kit/internal/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { useTracking } from 'shared/mortgageMiniCalculator/utils/tracking/useTracking';
import { EFieldName } from '../../../constants';
import { IFetchOffersProps } from '../../../hooks/useGetOffers';
import { setDefaultFormState } from '../../../redux';
import { selectFormDefaults } from '../../../selectors/calculator';
import { IFormValues, TBenefitProgram } from '../../../types';
import {
  calculatePercentageOfObjectCost,
  calculateValueFromPercentage,
  getDownPaymentFilterValue,
  normalizeMiniCalculatorFieldInput,
} from '../../../utils';
import { mapFieldNameToAnalyticsFieldName } from '../../../utils/mapFieldNameToAnalyticsFieldName';
import { ETypeChangeInput } from '../../../utils/tracking/types';
import {
  DOWN_PAYMENT_VALUES,
  MAX_DOWN_PAYMENT_PERCENTAGE,
  MAX_LOAN_TERM,
  MAX_OBJECT_COST,
  MIN_DOWN_PAYMENT_PERCENTAGE,
  MIN_LOAN_TERM,
  MIN_OBJECT_COST,
} from '../constants';

export const useFormCalculator = (fetchOffers: (params: IFetchOffersProps) => void) => {
  const dispatch = useDispatch();
  const initialFormValues = useSelector(selectFormDefaults);
  const { trackEditFieldValues } = useTracking();

  const [percentDownPayment, setPercentDownPayment] = useState(() => {
    if (!initialFormValues.objectCost) {
      return MIN_DOWN_PAYMENT_PERCENTAGE;
    }

    return (initialFormValues.downPayment / initialFormValues.objectCost) * 100;
  });

  const { config } = useApplicationContext();
  const isProgramFilterFieldEnabled = config.get<boolean>(ERuntimeSettings.programFilterFieldEnabled) ?? false;

  const {
    setValue,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
  });

  const { downPayment, objectCost, loanTermYears, benefitProgram } = watch();

  const effectiveObjectCost = objectCost > 0 ? objectCost : MIN_OBJECT_COST;

  const onSubmit = useCallback(
    async ({ objectCost: cost, downPayment: down, loanTermYears: term, benefitProgram: program }: IFormValues) => {
      /* Только те поля, которые форма правда редактирует: defaultValues react-hook-form сняты при
         монтировании, и regionId с realEstateType в них устаревают, когда хост меняет их на лету */
      const editedFields = {
        objectCost: cost,
        downPayment: down,
        loanTermYears: term,
        ...(isProgramFilterFieldEnabled ? { benefitProgram: program } : {}),
      };

      fetchOffers(editedFields);
      /* Форма живёт в react-hook-form, а тело save-offers и события собираются из стора: без этого
         в заявку и в аналитику уезжает расчёт, который человек уже изменил */
      dispatch(setDefaultFormState(editedFields));
    },
    [dispatch, fetchOffers, isProgramFilterFieldEnabled],
  );

  const downPaymentOptions = useMemo(() => {
    return DOWN_PAYMENT_VALUES.map(option => {
      const filterValue = getDownPaymentFilterValue(option);

      return {
        value: calculateValueFromPercentage(effectiveObjectCost, filterValue),
        label: `${option}%`,
        dataTestId: `down-payment-${option}`,
      };
    });
  }, [effectiveObjectCost]);

  const downPaymentChipValue = useMemo(() => {
    if (!downPayment) {
      return '';
    }

    // Проверяем, что процент от текущего downPayment попадает в диапазон,
    // например, от 20 до 20.1, чтобы включить предложения с неровными процентами
    const currentPercentage = (downPayment / effectiveObjectCost) * 100;

    for (const option of DOWN_PAYMENT_VALUES) {
      const optionNumber = Number(option);
      const filterValue = getDownPaymentFilterValue(option);

      // 0.001 на случай ошибки плавующей запятой
      if (currentPercentage >= optionNumber && currentPercentage <= filterValue + 0.001) {
        // Возвращаем значение, рассчитанное по filterValue для консистентности
        return calculateValueFromPercentage(effectiveObjectCost, filterValue);
      }
    }

    return '';
  }, [downPayment, effectiveObjectCost]);

  useEffect(() => {
    setValue(EFieldName.ObjectCost, initialFormValues.objectCost);
    setValue(EFieldName.DownPayment, initialFormValues.downPayment);
    setValue(EFieldName.loanTermYears, initialFormValues.loanTermYears);
    if (isProgramFilterFieldEnabled && initialFormValues.benefitProgram !== undefined) {
      setValue(EFieldName.benefitProgram, initialFormValues.benefitProgram);
    }
  }, [initialFormValues, setValue, isProgramFilterFieldEnabled]);

  useEffect(() => {
    if (initialFormValues.downPayment && initialFormValues.objectCost) {
      const newPercent = (initialFormValues.downPayment / initialFormValues.objectCost) * 100;
      setPercentDownPayment(newPercent);
    }
  }, [initialFormValues.downPayment, initialFormValues.objectCost]);

  useEffect(() => {
    if (objectCost <= 0) {
      return;
    }

    // Пересчитываем downPayment только если objectCost изменился
    // Не пересчитываем если текущее значение уже соответствует одному из процентов из опций
    const calculatedValue = calculateValueFromPercentage(objectCost, percentDownPayment);

    // Если пересчитанное значение совпадает с текущим (с допуском 1), не пересчитываем
    if (Math.abs(calculatedValue - downPayment) <= 1) {
      return;
    }

    // Проверяем, соответствует ли текущее значение одному из процентов из опций
    // Используем диапазон [option, filterValue] для проверки
    let matchesOption = false;
    const currentPercentage = (downPayment / objectCost) * 100;
    // Добавляем небольшой допуск для погрешности вычислений с плавающей точкой
    const EPSILON = 0.001;
    for (const option of DOWN_PAYMENT_VALUES) {
      const optionNumber = Number(option);
      const filterValue = getDownPaymentFilterValue(option);
      if (currentPercentage >= optionNumber && currentPercentage <= filterValue + EPSILON) {
        matchesOption = true;
        break;
      }
    }

    // Не пересчитываем если текущее значение соответствует одной из опций
    if (matchesOption) {
      return;
    }

    // Пересчитываем только если значение значительно отличается
    setValue(EFieldName.DownPayment, calculatedValue);
  }, [objectCost, percentDownPayment, setValue, downPayment]);

  const { debouncedSubmit, debouncedTrack } = useMemo(() => {
    return {
      debouncedSubmit: debounce(handleSubmit(onSubmit), 500),
      debouncedTrack: debounce(trackEditFieldValues, 500),
    };
  }, [handleSubmit, onSubmit, trackEditFieldValues]);

  const getHandlerByFieldName = useCallback(
    (fieldName: EFieldName, typeChange: ETypeChangeInput) => async (value: number | string | undefined) => {
      let trackingValue: number | string | TBenefitProgram = value as number | string | TBenefitProgram;

      if (fieldName === EFieldName.benefitProgram) {
        setValue(fieldName, value as TBenefitProgram);
      } else if (fieldName === EFieldName.DownPayment && typeChange === ETypeChangeInput.AUTO) {
        // При клике на чипс value - это числовое значение, вычисленное из процента
        const calculatedValue = value as number;
        const effectiveBase = objectCost > 0 ? objectCost : MIN_OBJECT_COST;
        const minDown = calculatePercentageOfObjectCost(effectiveBase, MIN_DOWN_PAYMENT_PERCENTAGE);
        const maxDown = calculatePercentageOfObjectCost(effectiveBase, MAX_DOWN_PAYMENT_PERCENTAGE);
        const clampedValue = Math.min(Math.max(calculatedValue, minDown), maxDown);
        const percentValue = (clampedValue / effectiveBase) * 100;
        setPercentDownPayment(percentValue);
        setValue(fieldName, clampedValue);
        trackingValue = clampedValue;
      } else if (fieldName === EFieldName.ObjectCost) {
        const next = normalizeMiniCalculatorFieldInput(value, MIN_OBJECT_COST, MAX_OBJECT_COST);
        setValue(fieldName, next);
        trackingValue = next;
      } else if (fieldName === EFieldName.loanTermYears) {
        const next = normalizeMiniCalculatorFieldInput(value, MIN_LOAN_TERM, MAX_LOAN_TERM);
        setValue(fieldName, next);
        trackingValue = next;
      } else if (fieldName === EFieldName.DownPayment) {
        const effectiveBase = objectCost > 0 ? objectCost : MIN_OBJECT_COST;
        const minDown = calculatePercentageOfObjectCost(effectiveBase, MIN_DOWN_PAYMENT_PERCENTAGE);
        const maxDown = calculatePercentageOfObjectCost(effectiveBase, MAX_DOWN_PAYMENT_PERCENTAGE);
        const next = normalizeMiniCalculatorFieldInput(value, minDown, maxDown);
        setValue(fieldName, next);
        setPercentDownPayment((next / effectiveBase) * 100);
        trackingValue = next;
      }

      const trackingFieldName = mapFieldNameToAnalyticsFieldName[fieldName];

      if (isValid) {
        await debouncedSubmit();
        debouncedTrack(typeChange, trackingValue, trackingFieldName);
      }
    },
    [setValue, objectCost, isValid, debouncedSubmit, debouncedTrack],
  );

  return {
    objectCost,
    downPayment,
    loanTermYears,
    downPaymentOptions,
    downPaymentChipValue,
    getHandlerByFieldName,
    isValid,
    benefitProgram,
  };
};
